import * as React from "react";
import { useParams } from "react-router";

import { Container, Grid, Pagination, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import TextFieldsIcon from "@mui/icons-material/TextFields";
import DescriptionIcon from "@mui/icons-material/Description";

import PaperFragment from "../../../helper-components/misc/PaperFragment";
import CustomForm from "../../../helper-components/form/CustomForm";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter";
import CustomButton from "../../../helper-components/input/CustomButton";
import PaperText from "../../../helper-components/misc/PaperText";

import { queryResource } from "../../../helper-functions/api/resourceQueries.js";
import { convertSecondsToFormattedTime, createUploadObject, isEmpty, isVimeo, isYoutube } from "../../../helper-functions/shared/miscUtils.js";
import { approveTranscript, queryTranscript, updateTranscript } from "../../../helper-functions/api/transcriptQueries.js";

import { getLanguage } from "../../../config/language/language.js";

/**
 * A page for reviewing a transcript before approving it.
 *
 * Includes:
 * - form for editing the transcript
 * - timestamps
 */
export default function TranscriptEdit(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.

    const resources = props?.backend?.resources?.data;
    const transcripts = props?.backend?.transcripts?.data;

    const [resource, setResource] = React.useState({}); // Set default value if resource is not available yet.
    const [resourceQuery, setResourceQuery] = React.useState(false);

    const [transcript, setTranscript] = React.useState({}); // Set default value if transcript is not available yet.
    const [transcriptQuery, setTranscriptQuery] = React.useState(false);

    const [editedMetaData, setEditedMetaData] = React.useState([]);
    const [showOriginal, setShowOriginal] = React.useState(false);

    const [currentPage, setCurrentPage] = React.useState(1);
    const entriesPerPage = 10;

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    // To reduce loading time, we only show a limited number of entries at a time.
    const hasRaw = Array.isArray(transcript?.meta_data_raw) && transcript.meta_data_raw.length > 0;
    const displayedMetaData = showOriginal && hasRaw ? transcript.meta_data_raw : editedMetaData;
    const paginatedMetaData = displayedMetaData.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);

    const punctuationRegex = /[^a-z0-9.,'€$£%-\s]/gi;
    const punctuationRegex2 = /(?<!\d)\.(?!\d)/gi;
    const punctuationRegex3 = /(?<!\d),(?!\d)/gi;

    const isTranscriptReady = !isEmpty(transcript) && !isEmpty(transcript?.meta_data);
    const isResourceReady = !isEmpty(resource);
    const cleanupBusy = ["AWAITING_CLEANUP", "IN_PROGRESS", "RECREATE"].includes(transcript?.status);
    const captionsReadOnly = showOriginal || cleanupBusy;

    const showApprove = transcript?.status === "AWAITING_APPROVAL" && !showOriginal;

    const regexInvalid = (text) => {
        const invalid = text.match(punctuationRegex) || text.match(punctuationRegex2) || text.match(punctuationRegex3);

        if (invalid) {
            console.log(`Invalid characters detected in transcript: ${invalid}\nText: ${text}`);
        }

        return invalid;
    };

    // Set the transcript, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleTranscript = transcripts.find((t) => t.id === parseInt(id));

        if (!isEmpty(possibleTranscript)) {
            setTranscriptQuery(true);
            setTranscript(possibleTranscript);
            setEditedMetaData(possibleTranscript?.meta_data || []); // Initialize edited transcript
        }

        if (isEmpty(possibleTranscript) && !transcriptQuery) {
            setTranscriptQuery(true);
            queryTranscript(props, { id });
        }
    }, [transcripts, transcript]);

    // Set the resource, and potentially query it if it's not available.
    React.useEffect(() => {
        if (!isTranscriptReady) return;

        const possibleResource = resources.find((r) => r.id === parseInt(transcript?.resource_id));

        if (!isEmpty(possibleResource)) {
            setResourceQuery(true);
            setResource(possibleResource);
        }

        if (isEmpty(possibleResource) && !resourceQuery) {
            setResourceQuery(true);
            queryResource(props, { id: transcript?.resource_id });
        }
    }, [resources, resource, transcript]);

    const handleTextChange = (index, event) => {
        const newEditedTranscript = [...editedMetaData];
        newEditedTranscript[index].text = event.target.value;
        setEditedMetaData(newEditedTranscript);
    };

    const showApproveDialog = () => {
        props.showDialog({
            title: "Approve transcript",
            message: `Are you sure you want to approve this transcript?

            Make sure the transcript is complete and accurate, as it will shortly begin further processing.`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handleApprove(confirm)
        });
    };

    const showPreUpdateDialog = () => {
        props.showDialog({
            title: "Update transcript",
            message: `Are you sure you want to update the transcript?

            The transcript will be reprocessed and automatically approved.`,
            actionTitle: "Continue",
            actionColor: "info",
            callback: (confirm) => showPunctuationDialog(confirm)
        });
    };

    const showPunctuationDialog = (confirm) => {
        if (!confirm) return;

        if (editedMetaData.some((slice, index) => regexInvalid(slice.text, index))) {
            props.showDialog({
                title: "Punctuation detected",
                message: `It seems like there are non-alphanumeric characters in the transcript.

                It is advisable to remove these characters before updating or approving the transcript. Otherwise, no automatic punctuation will be applied during further processing.`,
                actionTitle: "Remove punctuation and update",
                actionColor: "info",
                cancelTitle: "Keep punctuation and update",
                cancelColor: "error",
                callback: (confirm) => handleUpdate(confirm)
            });
        } else {
            handleUpdate();
        }
    };

    const handleUpdate = (removePunctuation = null) => {
        const body = createUploadObject(transcript, ["form"]);
        const metaData = JSON.parse(JSON.stringify(editedMetaData));

        if (removePunctuation) {
            metaData.forEach((slice) => {
                slice.text = slice.text.replace(punctuationRegex, "").replace(punctuationRegex2, "").replace(punctuationRegex3, "");
            });
        }

        if ([true, false].includes(removePunctuation)) {
            props.showSnackbar({ color: "info", message: removePunctuation ? "Punctuation removed from transcript" : "Punctuation kept in transcript" });
        }

        body.meta_data = metaData;
        updateTranscript(props, { body, id: transcript.id }, () => {
            props.browser.navigate("/database/transcripts");
        });
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        approveTranscript(props, { id: transcript.id }, () => {
            props.browser.navigate("/database/transcripts");
        });
    };

    const createUrl = (slice) => {
        if (!resource?.url) return "";

        if (isYoutube(resource?.url) && resource.type !== "SHORT") {
            return resource?.url + "&t=" + parseInt(slice.start);
        }

        if (isVimeo(resource?.url)) {
            return resource.url + "#t=" + parseInt(slice.start);
        }

        return resource.url;
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm title={getLanguage().TRANSCRIPT_EDIT_TITLE} subtitle={getLanguage().TRANSCRIPT_EDIT_SUBTITLE} icon={<TextFieldsIcon />} />
                </Grid>

                <Grid item xs={12}>
                    <PaperText bold center value={resource?.name || "Title: N/A"} />
                </Grid>

                {isTranscriptReady && hasRaw && (
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={showOriginal ? "original" : "cleaned"}
                            onChange={(event, value) => {
                                if (!value) return;
                                setShowOriginal(value === "original");
                            }}>
                            <ToggleButton value="cleaned">Cleaned</ToggleButton>
                            <ToggleButton value="original">Original</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                )}

                {isTranscriptReady &&
                    paginatedMetaData.map((slice, index) => {
                        const globalIndex = (currentPage - 1) * entriesPerPage + index; // Calculate the global index
                        const formattedStartTime = convertSecondsToFormattedTime(slice.start);
                        const formattedEndTime = convertSecondsToFormattedTime(slice.start + slice.duration);
                        const formattedDuration = convertSecondsToFormattedTime(slice.duration);

                        return (
                            <PaperFragment
                                key={index}
                                value={
                                    <Grid container spacing={1}>
                                        <Grid item xs={2} style={{ alignContent: "center" }}>
                                            <Typography variant="body2" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                                                <span>Start: </span>
                                                <span style={{ fontFamily: "monospace" }}>{formattedStartTime}</span>
                                            </Typography>

                                            <Typography variant="body2" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                                                <span>Duration: </span>
                                                <span style={{ fontFamily: "monospace" }}>{formattedDuration}</span>
                                            </Typography>

                                            <Typography variant="body2" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                                                <span>End: </span>
                                                <span style={{ fontFamily: "monospace" }}>{formattedEndTime}</span>
                                            </Typography>

                                            {isResourceReady && (isYoutube(resource?.url) || isVimeo(resource?.url)) && (
                                                <Typography variant="body2" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                                                    <a style={{ textDecoration: "inherit", color: "#fec01b" }} href={createUrl(slice)} target="_blank" rel="noreferrer">
                                                        [Open segment]
                                                    </a>
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid item xs={10}>
                                            {/* PS. Bug error in MUI code, multiline+fullWidth causes error when resizing */}
                                            <TextField
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                value={slice.text}
                                                onChange={(event) => handleTextChange(globalIndex, event)}
                                                disabled={captionsReadOnly}
                                            />
                                        </Grid>
                                    </Grid>
                                }
                            />
                        );
                    })}

                {isTranscriptReady && (
                    <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                        <Pagination count={Math.ceil(displayedMetaData.length / entriesPerPage)} page={currentPage} onChange={handlePageChange} color="primary" />
                    </Grid>
                )}

                {isTranscriptReady && showApprove && (
                    <Grid item xs={12} sm={showApprove ? 6 : 12}>
                        <CustomButton {...props} title={"Approve transcript..."} onClick={showApproveDialog} fullWidth variant="outlined" />
                    </Grid>
                )}

                {isTranscriptReady && (
                    <Grid item xs={12} sm={showApprove ? 6 : 12}>
                        <CustomButton
                            {...props}
                            title={editedMetaData.some((slice) => regexInvalid(slice.text)) ? "Update transcript..." : "Update transcript"}
                            onClick={showPreUpdateDialog}
                            fullWidth
                            disabled={captionsReadOnly}
                        />
                    </Grid>
                )}

                {isTranscriptReady && !isEmpty(transcript?.text) && !showApprove && (
                    <Grid item xs={12} mt={2}>
                        <CustomForm title={getLanguage().TRANSCRIPT_EDIT_VIEW_TITLE} subtitle={getLanguage().TRANSCRIPT_EDIT_VIEW_SUBTITLE} icon={<TextFieldsIcon />} />
                    </Grid>
                )}

                {isTranscriptReady && !isEmpty(transcript?.text) && !showApprove && (
                    <Grid item xs={12}>
                        <PaperText whiteSpace="pre-wrap" value={transcript?.text || "N/A"} p={1.5} />
                    </Grid>
                )}

                <NavigationFooter
                    rows={1}
                    options={[
                        {
                            path: `/database/transcripts`,
                            title: "Back to transcripts",
                            variant: "outlined",
                            icon: <TextFieldsIcon />
                        },
                        {
                            path: `/database/resources`,
                            title: "Resources",
                            variant: "outlined",
                            icon: <DescriptionIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
